// Master Apps — primitivos de chrome (porte de tokens.jsx).
import { type CSSProperties, type ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Icon,
  IconChevronR,
  IconCpu,
  IconGrid,
  IconLayers,
  IconLogout,
  IconMenu,
  IconMoon,
  type IconProps,
  IconServer,
  IconShield,
  IconSun,
  IconUsers,
  IconX,
} from '@/components/icons';
import { useMe } from '@/hooks/use-applications';
import { useViewport } from '@/hooks/use-viewport';
import { queryClient } from '@/lib/query-client';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  type Palette,
  rotuloCaixaAlta,
  STATUS,
} from '@/lib/theme';
import { useNavStore } from '@/store/nav';
import { getClerk, useSessionStore } from '@/store/session';
import { useScreenProps, useThemeStore } from '@/store/theme';

// Ícone de documento — montado aqui sobre o primitivo <Icon/> para servir ao
// acervo de documentos (nav + telas) sem tocar no set compartilhado.
export const IconDoc = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
      </>
    }
  />
);

// Ícone de apresentação — tela sobre tripé. Montado aqui, junto do IconDoc,
// para não inchar o set compartilhado com um ícone de uma única tela.
export const IconPresent = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <rect x="2" y="3" width="20" height="13" rx="2" />
        <line x1="12" y1="16" x2="12" y2="21" />
        <line x1="8" y1="21" x2="16" y2="21" />
      </>
    }
  />
);

// Ícone de gastos — moeda com o "$". Montado aqui, junto do IconDoc e do
// IconPresent, para não inchar o set compartilhado com um ícone de uma tela só.
export const IconCoin = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="12" cy="12" r="9" />
        <line x1="12" y1="6.5" x2="12" y2="17.5" />
        <path d="M14.8 9.4a2.4 2.4 0 0 0-2.3-1.4h-1a2.2 2.2 0 0 0 0 4.4h1.5a2.2 2.2 0 0 1 0 4.4h-1a2.4 2.4 0 0 1-2.3-1.4" />
      </>
    }
  />
);

export const NocMark = ({
  size = 36,
  onRed = false,
}: Readonly<{ size?: number; onRed?: boolean }>) => (
  <img
    src="/assets/masterboi-logo.png"
    alt="Masterboi"
    width={size}
    height={size}
    style={{
      display: 'block',
      borderRadius: 6,
      background: onRed ? '#fff' : 'transparent',
      padding: onRed ? 2 : 0,
      boxSizing: 'content-box',
      objectFit: 'contain',
    }}
  />
);

export const NocWordmark = ({
  color = '#0a0a0b',
  accent = NOC_RED,
  compact = false,
}: Readonly<{
  color?: string;
  accent?: string;
  compact?: boolean;
}>) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      fontFamily: fontSans,
      color,
    }}
  >
    <NocMark size={compact ? 26 : 32} />
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
      <div style={{ fontSize: compact ? 13 : 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
        Master<span style={{ color: accent }}>·</span>Apps
      </div>
      {!compact && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: '0.01em',
            opacity: 0.55,
            marginTop: 4,
            whiteSpace: 'nowrap',
          }}
        >
          Inventário de aplicações
        </div>
      )}
    </div>
  </div>
);

export function StatusDot({
  tone = 'ok',
  size = 8,
  pulse = false,
}: Readonly<{
  tone?: keyof typeof STATUS | string;
  size?: number;
  pulse?: boolean;
}>) {
  const c = STATUS[tone] ?? STATUS.unknown;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: c.solid,
        display: 'inline-block',
        flexShrink: 0,
        boxShadow: pulse ? `0 0 0 4px ${c.solid}33` : 'none',
        animation: pulse ? 'noc-pulse 1.6s ease-in-out infinite' : 'none',
      }}
    />
  );
}

export function Tag({
  children,
  dark,
  tone = 'neutral',
}: Readonly<{
  children: ReactNode;
  dark: boolean;
  tone?: 'neutral' | 'red' | 'blue';
}>) {
  const p = makePalette(dark);
  const tones = {
    neutral: { bg: p.surface2, fg: p.textMuted, br: p.border },
    red: {
      bg: dark ? '#C8102E22' : '#fef2f2',
      fg: dark ? '#fca5a5' : '#991b1b',
      br: dark ? '#C8102E44' : '#fecaca',
    },
    blue: {
      bg: dark ? '#3b82f622' : '#eff6ff',
      fg: dark ? '#93c5fd' : '#1d4ed8',
      br: dark ? '#3b82f644' : '#bfdbfe',
    },
  };
  const t = tones[tone];
  return (
    <span
      style={{
        ...rotuloCaixaAlta,
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 6px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.br}`,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export function Card({
  children,
  dark,
  style = {},
  padding = 16,
  title,
  subtitle,
  action,
  dense = false,
}: Readonly<{
  children: ReactNode;
  dark: boolean;
  style?: CSSProperties;
  padding?: number;
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  dense?: boolean;
}>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 8,
        ...style,
      }}
    >
      {(title || action) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: dense ? '10px 14px' : '14px 16px',
            borderBottom: `1px solid ${p.border}`,
          }}
        >
          <div style={{ minWidth: 0 }}>
            {title && (
              <div
                style={{ fontSize: 13, fontWeight: 600, color: p.text, letterSpacing: '-0.005em' }}
              >
                {title}
              </div>
            )}
            {subtitle && (
              <div style={{ ...numeroTabular, fontSize: 11, color: p.muted, marginTop: 2 }}>
                {subtitle}
              </div>
            )}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      <div style={{ padding: dense ? 12 : padding }}>{children}</div>
    </div>
  );
}

interface NavItem {
  id: string;
  icon: ReactNode;
  label: string;
  badge?: number;
  admin?: boolean;
}

const NAV_ROUTES: Record<string, string> = {
  catalog: '/catalog',
  presentation: '/admin/apresentacao',
  inventory: '/admin/inventory',
  costs: '/admin/gastos',
  internal: '/admin/internal',
  documents: '/admin/documents',
  audit: '/admin/audit',
  stacks: '/admin/stacks',
  users: '/admin/users',
};

// Segmentado sol/lua de tema — autocontido (lê o store), reutilizável em
// qualquer tela, inclusive fora do chrome (login, primeiro acesso).
export function ThemeToggle() {
  const dark = useThemeStore((s) => s.dark);
  const toggleDark = useThemeStore((s) => s.toggleDark);
  const { accent } = useScreenProps();
  const p = makePalette(dark);
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 2,
        gap: 2,
        background: p.surface2,
        border: `1px solid ${p.border}`,
        borderRadius: 999,
      }}
    >
      {(
        [
          { id: 'light', icon: <IconSun size={13} />, title: 'Modo claro' },
          { id: 'dark', icon: <IconMoon size={13} />, title: 'Modo escuro' },
        ] as const
      ).map((opt) => {
        const active = dark === (opt.id === 'dark');
        return (
          <button
            key={opt.id}
            type="button"
            title={opt.title}
            aria-label={opt.title}
            aria-pressed={active}
            onClick={() => {
              if (!active) toggleDark();
            }}
            style={{
              width: 24,
              height: 22,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 999,
              border: 'none',
              cursor: active ? 'default' : 'pointer',
              background: active ? p.surface : 'transparent',
              color: active ? accent : p.muted,
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
              transition: 'background 160ms, color 160ms',
            }}
          >
            {opt.icon}
          </button>
        );
      })}
    </div>
  );
}

export function Sidebar({
  active = 'catalog',
  dark,
  accent = NOC_RED,
  role = 'admin',
}: Readonly<{
  active?: string;
  dark: boolean;
  accent?: string;
  role?: 'admin' | 'collab';
}>) {
  const p = makePalette(dark);
  const navigate = useNavigate();
  const signOut = useSessionStore((s) => s.signOut);
  const { data: me } = useMe();

  // Perfil real (Clerk via /api/me) — nada de persona hardcoded.
  const displayName = me?.name ?? me?.email?.split('@')[0] ?? '';
  const initials = (() => {
    const fromName = me?.name
      ?.trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
    if (fromName) return fromName;
    if (me?.email) return me.email[0].toUpperCase();
    return '·';
  })();

  // Só o catálogo: "Meus apps" e "Favoritos" saíram do menu — favoritar segue
  // existindo (estrela no card) e os favoritos aparecem no TOPO do catálogo,
  // em vez de numa tela separada.
  const baseItems: NavItem[] = [{ id: 'catalog', icon: <IconGrid size={18} />, label: 'Catálogo' }];
  const adminItems: NavItem[] = [
    { id: 'inventory', icon: <IconLayers size={18} />, label: 'Inventário', admin: true },
    { id: 'presentation', icon: <IconPresent size={18} />, label: 'Apresentação', admin: true },
    { id: 'internal', icon: <IconServer size={18} />, label: 'Apps internos', admin: true },
    { id: 'documents', icon: <IconDoc size={18} />, label: 'Documentos', admin: true },
    { id: 'audit', icon: <IconShield size={18} />, label: 'Auditoria', admin: true },
    { id: 'stacks', icon: <IconCpu size={18} />, label: 'Stacks', admin: true },
    { id: 'costs', icon: <IconCoin size={18} />, label: 'Gastos', admin: true },
    { id: 'users', icon: <IconUsers size={18} />, label: 'Usuários', admin: true },
  ];
  const items = role === 'admin' ? [...baseItems, ...adminItems] : baseItems;

  // ── Modo gaveta (abaixo de 1024px) ────────────────────────────────────────
  // No desktop a barra é uma coluna de 220px no fluxo, exatamente como antes.
  // No compacto ela sai do fluxo e vira painel sobreposto: 220px de rail em
  // 375px de tela deixariam 155px para o conteúdo.
  const { compact } = useViewport();
  const open = useNavStore((s) => s.open);
  const closeNav = useNavStore((s) => s.close);

  // Esc fecha a gaveta. Só assina quando ela está aberta — ouvinte global vivo
  // no desktop roubaria o Esc das telas que já o usam (o cadastro cancela com
  // Esc, e o modal do deck fecha com Esc).
  useEffect(() => {
    if (!compact || !open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeNav();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [compact, open, closeNav]);

  // Fechada no compacto: nada é renderizado. Quem abre é o <NavToggle/> da barra
  // superior. Manter o <aside> escondido com `display:none` não bastaria — ele
  // continuaria na ordem de tabulação em alguns navegadores.
  if (compact && !open) return null;

  const aside = (
    <aside
      style={{
        width: compact ? 264 : 220,
        flexShrink: 0,
        background: p.surface,
        borderRight: `1px solid ${p.border}`,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 14px',
        ...(compact
          ? {
              // `fixed` + `--app-vh`: no celular a barra de endereço come parte de
              // `100vh`, e a gaveta ficaria com o rodapé (usuário/sair) cortado
              // abaixo da dobra. A variável está em `globals.css` e já resolve o
              // fallback para navegador sem `dvh` — ver o comentário de lá.
              position: 'fixed',
              inset: '0 auto 0 0',
              height: 'var(--app-vh)',
              maxWidth: '85vw',
              zIndex: 60,
              overflowY: 'auto',
              boxShadow: '0 24px 60px -20px rgba(0,0,0,0.45)',
            }
          : {
              // No desktop o shell das telas é `overflow: hidden`: em viewport
              // BAIXO (notebook FHD com escala 125% sobra ~690px úteis) a
              // navegação inteira não cabia e o rodapé (Tema/usuário) era
              // engolido pelo corte. Mesma saída da gaveta acima: a própria
              // barra rola quando falta altura — em tela alta a barra de
              // rolagem nem nasce.
              overflowY: 'auto',
              scrollbarWidth: 'thin',
            }),
      }}
    >
      {compact && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <button
            type="button"
            onClick={closeNav}
            aria-label="Fechar navegação"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${p.border}`,
              background: p.surface2,
              color: p.textMuted,
              cursor: 'pointer',
            }}
          >
            <IconX size={16} />
          </button>
        </div>
      )}
      <div style={{ padding: '0 6px 24px 6px' }}>
        <NocWordmark color={p.text} accent={accent} compact={false} />
      </div>

      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.14em',
          color: p.muted,
          textTransform: 'uppercase',
          padding: '0 10px',
          marginBottom: 8,
        }}
      >
        Catálogo
      </div>

      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((it, idx) => {
          const showAdminLabel = it.admin && (idx === 0 || !items[idx - 1].admin);
          const isActive = it.id === active;
          return (
            <div key={it.id}>
              {showAdminLabel && (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    color: p.muted,
                    textTransform: 'uppercase',
                    padding: '12px 10px 6px',
                  }}
                >
                  Administração
                </div>
              )}
              {/* Item de navegação é controle de verdade (leva a outra rota):
                  <button> em vez de <div onClick>, para responder a Enter/Espaço
                  e entrar na ordem de tabulação sem handler de teclado ad hoc.
                  As bordas vão em longhand (o botão só desenha a da esquerda) e
                  width/textAlign/fontFamily reproduzem o box do <div>. */}
              <button
                type="button"
                aria-current={isActive ? 'page' : undefined}
                onClick={() => {
                  const route = NAV_ROUTES[it.id];
                  if (route) navigate(route);
                  // Navegar fecha a gaveta: no compacto ela cobre a tela, e ficar
                  // aberta sobre o destino esconderia justamente o que se pediu.
                  // No desktop `close()` não tem efeito visível (a barra é fixa).
                  closeNav();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 10px',
                  width: '100%',
                  textAlign: 'left',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: fontSans,
                  color: isActive ? p.text : p.textMuted,
                  background: isActive ? p.surface2 : 'transparent',
                  borderTop: 'none',
                  borderRight: 'none',
                  borderBottom: 'none',
                  borderLeft: `2px solid ${isActive ? accent : 'transparent'}`,
                  paddingLeft: isActive ? 8 : 10,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span style={{ color: isActive ? accent : p.muted, display: 'flex' }}>
                  {it.icon}
                </span>
                <span style={{ flex: 1 }}>{it.label}</span>
                {it.badge != null && (
                  <span
                    style={{
                      ...numeroTabular,
                      background: accent,
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 999,
                    }}
                  >
                    {it.badge}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </nav>

      <div style={{ flex: 1 }} />

      {/* Toggle claro/escuro — segmentado sol/lua, junto ao bloco do usuário. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 10px',
          color: p.textMuted,
          fontSize: 12,
          fontWeight: 500,
          fontFamily: fontSans,
          userSelect: 'none',
        }}
      >
        <span>Tema</span>
        <ThemeToggle />
      </div>

      <div
        style={{
          padding: '12px 10px',
          borderTop: `1px solid ${p.border}`,
          marginTop: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {me?.imageUrl ? (
          <img
            src={me.imageUrl}
            alt={displayName || 'Avatar'}
            width={32}
            height={32}
            style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: accent,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: p.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName || '\u00A0'}
          </div>
          <div
            style={{
              fontSize: 10,
              color: p.muted,
              fontFamily: fontMono,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {me?.email ?? '\u00A0'}
          </div>
        </div>
        <button
          type="button"
          title="Sair"
          onClick={() => {
            signOut();
            // Sair também esvazia o cache do TanStack Query: sem isso, em
            // máquina compartilhada, o próximo usuário a logar sem F5 veria por
            // um instante o perfil/apps do anterior servidos do cache.
            queryClient.clear();
            // A sessão local já caiu e o usuário já vai para /login: o encerramento
            // no Clerk é assíncrono e não bloqueia a saída. Mesmo assim a promessa
            // é tratada com `.catch` em vez de descartada com `void` — promessa
            // rejeitada em silêncio esconde "saí mas continuo logado no Clerk".
            getClerk()
              ?.signOut()
              .catch((e: unknown) => {
                console.error('[Sidebar] falha ao encerrar a sessão no Clerk:', e);
              });
            navigate('/login');
          }}
          style={{
            color: p.muted,
            display: 'flex',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none',
            padding: 0,
          }}
        >
          <IconLogout size={15} />
        </button>
      </div>
    </aside>
  );

  if (!compact) return aside;

  // Gaveta aberta: camada de escurecimento que fecha ao clique. `aria-hidden` +
  // sem `role` de propósito — é o mesmo padrão já usado no InfoPopover e no modal
  // do deck: elemento fora da árvore de acessibilidade não precisa de handler de
  // teclado (quem fecha pelo teclado é o Esc e o botão X, ambos acima).
  return (
    <>
      <div
        aria-hidden="true"
        onClick={closeNav}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 55,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(2px)',
        }}
      />
      {aside}
    </>
  );
}

/**
 * Hambúrguer que abre a gaveta. Só existe abaixo de 1024px — no desktop devolve
 * `null`, então as barras podem chamá-lo sem condicional e sem mudar o layout.
 *
 * Mora aqui, e não em cada tela, porque a barra superior de várias telas é
 * própria (o catálogo tem a sua, o cadastro tem a sua) e todas precisam do mesmo
 * botão com o mesmo alvo de toque de 40px.
 */
export function NavToggle({ dark }: Readonly<{ dark: boolean }>) {
  const { compact } = useViewport();
  const toggle = useNavStore((s) => s.toggle);
  const p = makePalette(dark);
  if (!compact) return null;
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Abrir navegação"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: 8,
        border: `1px solid ${p.border}`,
        background: p.surface2,
        color: p.text,
        cursor: 'pointer',
      }}
    >
      <IconMenu size={18} />
    </button>
  );
}

export function TopBar({
  title,
  subtitle,
  dark,
  right,
  breadcrumb,
}: Readonly<{
  title: ReactNode;
  subtitle?: ReactNode;
  dark: boolean;
  right?: ReactNode;
  breadcrumb?: string[];
}>) {
  const p = makePalette(dark);
  const { compact, mobile } = useViewport();
  return (
    <div
      style={{
        minHeight: 64,
        flexShrink: 0,
        borderBottom: `1px solid ${p.border}`,
        background: p.surface,
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 10 : 16,
        padding: compact ? '10px 14px' : '12px 24px',
        // No celular título e ações empilham: lado a lado, um título de duas
        // palavras já empurrava os botões para fora da tela.
        flexWrap: mobile ? 'wrap' : 'nowrap',
      }}
    >
      <NavToggle dark={dark} />
      {/*
        `flexBasis: 100%` no celular joga as AÇÕES para a linha de baixo. Sem
        isso o `flex: 1` + `minWidth: 0` daqui faz o título encolher até virar
        reticências para caber os botões ao lado — o mesmo defeito que a coluna
        de nome da tabela do inventário tinha em tela estreita.
      */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          flexBasis: mobile ? '100%' : undefined,
        }}
      >
        {breadcrumb && breadcrumb.length > 0 && (
          <div
            style={{
              fontSize: 11,
              color: p.muted,
              fontWeight: 500,
              marginBottom: 3,
              fontFamily: fontSans,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {breadcrumb.map((b, i) => (
              <span key={b} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {i > 0 && <IconChevronR size={12} />}
                <span style={{ color: i === breadcrumb.length - 1 ? p.textMuted : p.muted }}>
                  {b}
                </span>
              </span>
            ))}
          </div>
        )}
        <div style={{ fontSize: 17, fontWeight: 600, color: p.text, letterSpacing: '-0.015em' }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 12.5, color: p.muted, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {/* As ações também embrulham: telas com três botões (Exportar, Nova
          aplicação, filtros) não caberiam numa linha de 375px. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          width: mobile ? '100%' : undefined,
        }}
      >
        {right}
      </div>
    </div>
  );
}

export function FilterChip({
  label,
  count,
  active = false,
  dark,
  onClick,
}: Readonly<{
  label: string;
  count?: number;
  active?: boolean;
  dark: boolean;
  accent?: string;
  onClick?: () => void;
}>) {
  const p = makePalette(dark);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 30,
        padding: '0 12px',
        background: active ? p.text : 'transparent',
        color: active ? p.surface : p.textMuted,
        border: `1px solid ${active ? p.text : p.border}`,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: fontSans,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      {count != null && (
        <span
          style={{
            ...numeroTabular,
            fontSize: 10,
            fontWeight: 700,
            opacity: active ? 0.7 : 0.55,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function ButtonGhost({
  children,
  dark,
  onClick,
}: Readonly<{
  children: ReactNode;
  dark: boolean;
  onClick?: () => void;
}>) {
  const p: Palette = makePalette(dark);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 32,
        padding: '0 12px',
        background: 'transparent',
        border: `1px solid ${p.border}`,
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        color: p.textMuted,
        fontFamily: fontSans,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {children}
    </button>
  );
}
